import { z } from 'zod'
import { Household, HouseholdJoinCode } from './household.ts'
import { SessionId } from './ids.ts'
import { User } from './user.ts'

// 5-digit numeric code, stored as a string to preserve leading zeros.
export const AuthCodeValue = z.string().regex(/^\d{5}$/)
export type AuthCodeValue = z.infer<typeof AuthCodeValue>

// What a pending code is *for*. Without this, a code emailed to prove you own
// a new address could be fed straight into POST /auth/verify, which — with
// registration open — would found a separate account on it. Both verifiers
// check it. Optional-with-default so rows written before this existed parse.
export const AuthCodePurpose = z.enum(['signin', 'email_change'])
export type AuthCodePurpose = z.infer<typeof AuthCodePurpose>

export const AuthCode = z.object({
	email: z.email(),
	code: AuthCodeValue,
	createdAt: z.iso.datetime(),
	expiresAt: z.number().int().positive(),
	attemptsRemaining: z.number().int().min(0).max(5),
	purpose: AuthCodePurpose.optional().default('signin'),
})
export type AuthCode = z.infer<typeof AuthCode>

// What the person said they came to do on the sign-in screen. The server only
// uses it to give an honest error — 'signin' with an unknown email is a typo,
// not a cue to quietly found a household nobody asked for.
export const AuthIntent = z.enum(['signin', 'register'])
export type AuthIntent = z.infer<typeof AuthIntent>

export const AuthRequestBody = z.object({
	email: z.email(),
	intent: AuthIntent.default('signin'),
})
export type AuthRequestBody = z.infer<typeof AuthRequestBody>

export const AuthRequestResponse = z.object({
	ok: z.literal(true),
})
export type AuthRequestResponse = z.infer<typeof AuthRequestResponse>

// `returnToken` is set only by the iOS build, which can't use the cookie and
// sends the session ID back as a Bearer token instead. The website never sets
// it, so the ID never reaches the website's JavaScript.
export const AuthVerifyBody = z.object({
	email: z.email(),
	code: AuthCodeValue,
	returnToken: z.boolean().optional(),
})
export type AuthVerifyBody = z.infer<typeof AuthVerifyBody>

export const AuthVerifyResponse = z.object({
	user: User,
	household: Household,
	token: SessionId.optional(),
})
export type AuthVerifyResponse = z.infer<typeof AuthVerifyResponse>

// Which Apple client issued the authorization code. The server needs it to
// pick the client ID (bundle ID or Services ID) for the code exchange.
export const AppleClient = z.enum(['ios', 'web'])
export type AppleClient = z.infer<typeof AppleClient>

// POST /auth/apple. Only the authorization code crosses over; the server
// redeems it with Apple directly, so the client never vouches for an identity
// token. Answers with AuthVerifyResponse.
export const AppleSignInBody = z.object({
	code: z.string().min(1),
	client: AppleClient,
	intent: AuthIntent,
	returnToken: z.boolean().optional(),
})
export type AppleSignInBody = z.infer<typeof AppleSignInBody>

export const AuthMeResponse = z.object({
	user: User.nullable(),
	household: Household.nullable(),
})
export type AuthMeResponse = z.infer<typeof AuthMeResponse>

// Onboarding forks: found a brand-new household (name it), or join an existing
// one by its code. Both name the user. Discriminated on `mode` so each branch
// carries exactly the fields it needs.
const DisplayName = z.string().min(1).max(100)

export const OnboardingBody = z.discriminatedUnion('mode', [
	z.object({
		mode: z.literal('create'),
		displayName: DisplayName,
		householdName: z.string().min(1).max(100),
	}),
	z.object({
		mode: z.literal('join'),
		displayName: DisplayName,
		// Lenient input: trim + uppercase before validating the shape.
		code: z.string().trim().toUpperCase().pipe(HouseholdJoinCode),
	}),
])
export type OnboardingBody = z.infer<typeof OnboardingBody>

export const OnboardingResponse = z.object({
	user: User,
	household: Household,
})
export type OnboardingResponse = z.infer<typeof OnboardingResponse>
